import React, { useState, useEffect, useMemo } from "react";
import { X, ChevronRight, ChevronLeft, Building2, User2, Search } from "lucide-react";
import Select from "react-select";
import { getCustomSelectStyles } from "../../../styles/selectStyles";
import styles from "./UserModal.module.css";
import userService from "../../../services/user.service";
import clientService from "../../../services/client.service";
import CommonPagination from "../../Common/CommonPagination"; // Added import
import Loading from "../../Common/Loading";
import Toast, { ToastType } from '../../Common/Toast';
import { set } from "react-datepicker/dist/date_utils";

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    email: string;
    username: string;
    first_name: string;
    middle_name?: string;
    last_name: string;
    emp_id?: string;
    date_of_birth?: string;
    password?: string;
    phone_country_code?: string;
    phone_number?: string;
    role_ids: string[];
    supervisor_id?: string;
    client_id?: string | null; // Added | null for explicit unmapping
  }) => void;
  initialData?: {
    id?: string;
    email: string;
    username: string;
    first_name: string;
    middle_name?: string;
    last_name: string;
    emp_id?: string;
    date_of_birth?: string;
    password?: string;
    phone_country_code?: string;
    phone_number?: string;
    roles: Array<{ id: string; name: string }>;
    supervisor_id?: string;
    client_id?: string;
    client_name?: string;
  };
  title?: string;
  roles?: Array<{ id: string; name: string }>;
  // supervisors?: Array<{ id: string; name: string }>;
  clientName?: string;
  isClientUser?: boolean;
  clientAdminRoleId?: string;
  allowUserTypeSelection?: boolean; // only org role
  canChooseUserType?: boolean;
  isLoading?: boolean;
}

const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  title,
  roles = [],
  // supervisors = [],
  clientName,
  isClientUser,
  clientAdminRoleId,
  allowUserTypeSelection,
  canChooseUserType,   // ← add here
  isLoading,
}) => {
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [empId, setEmpId] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [password, setPassword] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("+91");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  // const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  // const [selectedSupervisor, setSelectedSupervisor] = useState<string>('');
  const [userRoles, setUserRoles] = useState<
    { value: string; label: string }[]
  >([]);

  const isClientMode = isClientUser;

  const [supervisorRole, setSupervisorRole] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const [supervisorOptions, setSupervisorOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const [selectedSupervisor, setSelectedSupervisor] = useState<string | null>(
    null,
  );
  const [loadingSupervisors, setLoadingSupervisors] = useState(false);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Client Selection State
  const [clients, setClients] = useState<any[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [userType, setUserType] = useState<"internal" | "client" | null>(null);
  const isEditMode = !!initialData?.id;

  const [selectedClient, setSelectedClient] = useState<any>(null);

  // Search & Pagination for Clients
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    // console.log("allowUserTypeSelection in modal:", allowUserTypeSelection);
  }, [allowUserTypeSelection]);

  const goBackToType = () => {
    setUserType(null);
    setSelectedClient(null);
    setStep(0);
  };

  useEffect(() => {
    const initializeData = async () => {
      if (initialData) {
        setEmail(initialData.email || "");
        setUsername(initialData.username || "");
        setFirstName(initialData.first_name || "");
        setMiddleName(initialData.middle_name || "");
        setLastName(initialData.last_name || "");
        setEmpId(initialData.emp_id || "");
setDateOfBirth(
  initialData.date_of_birth
    ? initialData.date_of_birth.split("T")[0]
    : ""
);        setPhoneCountryCode((initialData as any).phone_country_code || "+91");
        setPhoneNumber((initialData as any).phone_number || "");
        setUserRoles(
          (initialData.roles || []).map((r) => ({ value: r.id, label: r.name })),
        );
        setSelectedSupervisor(initialData.supervisor_id || null);
        setPassword("");

        // CHECK FOR CLIENT MAPPING
        if (initialData.client_id) {
          try {
            const allClients = await clientService.getAllClients();
            setClients(allClients);
            const matched = allClients.find((c: any) => c.id === initialData.client_id);
            setSelectedClient({
              id: initialData.client_id,
              name: matched?.name || initialData.client_name || "Unknown Client",
              npi: matched?.npi
            });
          } catch (e) {
            console.error("Error fetching client details", e);
            setSelectedClient({ id: initialData.client_id, name: "Unknown Client" });
          }
          setUserType("client");
        } else {
          setUserType("internal");
          setSelectedClient(null);
        }
      } else {
        setEmail("");
        setUsername("");
        setFirstName("");
        setMiddleName("");
        setLastName("");
        setEmpId("");
        setDateOfBirth("");
        setPassword("");
        setPhoneCountryCode("+91");
        setPhoneNumber("");
        setUserRoles([]);
        setSelectedSupervisor("");
        setUserType(null);
        setSelectedClient(null);
      }

      // Initial Step Logic
      if (initialData?.id) {
        // EDIT MODE: Skip type selection, go to form directly
        setStep(1);
      } else if (allowUserTypeSelection) {
        setStep(0);
      } else {
        // Fallback if type selection not allowed (e.g. non-org user?)
        // If editing, go to form. If creating, go to form (internal default).
        setStep(1);
        if (!initialData?.id) setUserType("internal");
      }

      setErrors({});
    };

    initializeData();
  }, [initialData, isOpen, allowUserTypeSelection]);

useEffect(() => {
  if (!supervisorRole) {
    setSupervisorOptions([]);
    return;
  }

  let cancelled = false;
  setLoadingSupervisors(true);

  userService
    .getUsersByRole(supervisorRole.value)
    .then(users => {
      if (cancelled) return;
      console.log("API CALLED FOR ROLE:", supervisorRole.value);
  console.log("USERS RECEIVED:", users);

      setSupervisorOptions(
        users.map((u: any) => ({
          value: u.id,
          label: `${u.first_name} ${u.last_name} (${u.username})`
        }))
      );
    })
    .catch(() => {
      if (!cancelled) setSupervisorOptions([]);
    })
    .finally(() => {
      if (!cancelled) setLoadingSupervisors(false);
    });

  return () => {
    cancelled = true;
  };
}, [supervisorRole]);


  // Removed the duplicate useEffect for step logic that was here before, 
  // checking it is now handled in the main initialization useEffect above.

  // Fetch clients when switching to client type
  useEffect(() => {
    if (userType === "client") {
      if (clients.length > 0) return;
      setLoadingClients(true);
      // Using getAllClients or getClients with large number to support client-side pagination as requested
      clientService.getAllClients()
        .then(data => {
          setClients(data);
        })
        .catch(err => console.error("Failed to load clients", err))
        .finally(() => setLoadingClients(false));
    }
  }, [userType, clients.length]);

  const validateStep1 = () => {
    const newErrors: { [key: string]: string } = {};

    if (!(email || "").trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = "Invalid email format";

    if (!(username || "").trim()) newErrors.username = "Username is required";
    else if (username && username.length < 3)
      newErrors.username = "Username must be at least 3 characters";
    else if (username && username.length > 50)
      newErrors.username = "Username cannot exceed 50 characters";

    if (!(firstName || "").trim()) newErrors.firstName = "First name is required";
    else if (firstName && firstName.length > 50)
      newErrors.firstName = "First name cannot exceed 50 characters";

    if (lastName && lastName.length > 50)
      newErrors.lastName = "Last name cannot exceed 50 characters";
    if (!(empId || "").trim()) newErrors.empId = "Employee ID is required";
    else if (empId && empId.length > 50)
      newErrors.empId = "Employee ID cannot exceed 50 characters";
    if (dateOfBirth && !/^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth)) {
      newErrors.dateOfBirth = "Invalid date format. Please use YYYY-MM-DD.";
    }
    if (middleName && middleName.length > 50)
      newErrors.middleName = "Middle name cannot exceed 50 characters";

    if (!initialData?.id && !(password || "").trim())
      newErrors.password = "Password is required";
    else if (!initialData?.id && password && password.length < 6)
      newErrors.password = "Password must be at least 6 characters";

    if (phoneNumber && !/^\d{10,15}$/.test(phoneNumber)) {
      newErrors.phoneNumber = "Phone number must be 10-15 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleBack = () => {
    if (step === 3) {
      // Back from Client Selection
      goBackToType();
    } else if (step === 2) {
      // Back from Roles -> Details
      setStep(1);
    } else if (step === 1 && userType === "client") {
      setStep(3); // Go back to client selection if coming from there
    } else if (step === 1 && isEditMode && allowUserTypeSelection) {
      setStep(0);
    } else if (step === 1 && !isEditMode && allowUserTypeSelection) {
      setStep(0);
    } else if (step === 1) {
      // If not allowed to select type, do nothing or close?
      // Actually if we can't select type, we are stuck on step 1 or we go back to close?
      // Usually back button shouldn't be visible if step 1 and no type selection.
      // But if it is visible, it should probably close or do nothing.
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep1()) return;

    const payload: any = {
      email: (email || "").trim(),
      username: (username || "").trim(),
      first_name: (firstName || "").trim(),
      middle_name: (middleName || "").trim() || undefined,
      last_name: (lastName || "").trim() || undefined,
      emp_id: (empId || "").trim(),
      date_of_birth: dateOfBirth || undefined,
      phone_country_code: phoneCountryCode,
      phone_number: phoneNumber || undefined,
      supervisor_id: selectedSupervisor || undefined,
    };

    if (!initialData?.id) payload.password = password;

    // 🔥 CLIENT USER
    if (userType === "client") {
      if (!selectedClient) {
        alert("Select client");
        return;
      }

      payload.client_id = selectedClient.id;
    }

    // 🔥 INTERNAL USER
    if (userType === "internal") {
      payload.role_ids = userRoles.map(r => r.value);
      // Explicitly remove client_id if we switched to Internal
      payload.client_id = null;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(payload);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Logic for Client Selection Step (Step 3)
  const filteredClients = useMemo(() => {
    if (!clients) return [];
    return clients.filter(
      (c) =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.npi && c.npi.includes(searchTerm))
    );
  }, [clients, searchTerm]);

  const paginatedClients = useMemo(() => {
    const start = currentPage * itemsPerPage;
    return filteredClients.slice(start, start + itemsPerPage);
  }, [filteredClients, currentPage, itemsPerPage]);

  const handleClientSelect = (client: any) => {
    setSelectedClient(client);
    // setStep(1); // Removed auto-advance
  };

  const handlePageChange = (selectedItem: { selected: number }) => {
    setCurrentPage(selectedItem.selected);
  };

  if (!isOpen) return null;

  const roleOptions = roles.map((r) => ({ value: r.id, label: r.name }));
  const countryCodeOptions = [
    { value: "+91", label: "+91" },
    { value: "+1", label: "+1" },
  ];

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.content} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>
            {title}
            {step === 3 ? " - Select Client" : !isClientMode && !isEditMode && step !== 0 && `- Step ${step} of 2`}
            {clientName && (
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "normal",
                  color: "#6b7280",
                  marginTop: "4px",
                }}
              >
                For client: {clientName}
              </div>
            )}
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formContent} style={{ position: 'relative', minHeight: '200px' }}>
            {isLoading && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255, 255, 255, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                borderRadius: '8px'
              }}>
                <div className={styles.loader}>Loading form data...</div>
              </div>
            )}

            {step === 0 && !initialData?.id && allowUserTypeSelection && (
              <div className={styles.typeSelector}>
                <button
                  type="button"
                  className={`${styles.typeCard} ${userType === "internal" ? styles.active : ""}`}
                  onClick={() => {
                    setUserType("internal");
                    setSelectedClient(null);
                    setStep(1); // skip client step
                  }}
                >
                  <div className={styles.cardContent}>
                    <div className={styles.iconWrapper}>
                      <Building2 size={32} className={styles.icon} />
                    </div>
                    <h4>Internal User</h4>
                    <p>For organisation users</p>
                  </div>
                </button>

                <button
                  type="button"
                  className={`${styles.typeCard} ${styles.client} ${userType === "client" ? styles.active : ""}`}
                  onClick={() => {
                    setUserType("client");
                    setStep(3); // Go to updated Client Selection Step
                  }}
                >
                  <div className={styles.cardContent}>
                    <div className={styles.iconWrapper}>
                      <User2 size={32} className={styles.icon} />
                    </div>
                    <h4>Client User</h4>
                    <p>For client-side access & roles</p>
                  </div>
                </button>
              </div>
            )}

            {step === 3 && (
              <div className={styles.clientSelectionContainer}>
                <div className={styles.searchWrapper}>
                  <Search className={styles.searchIcon} size={16} />
                  <input
                    type="text"
                    placeholder="Search by name or NPI..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(0);
                    }}
                    className={styles.searchInput}
                  />
                </div>

                {loadingClients ? (
                  <Loading message="Loading clients..." />
                  // <div className={styles.loader}>Loading clients...</div>
                ) : filteredClients.length === 0 ? (
                  <div className={styles.emptyState}>No clients found.</div>
                ) : (
                  <>
                    <div className={styles.tableWrapper}>
                      <table className={styles.clientTable}>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>NPI</th>
                            <th>Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedClients.map((client) => (
                            <tr
                              key={client.id}
                              onClick={() => handleClientSelect(client)}
                              className={`${styles.clientRow} ${selectedClient?.id === client.id ? styles.selectedRow : ""}`}
                            >
                              <td>{client.name}</td>
                              <td>{client.npi || "-"}</td>
                              <td><span className={styles.badge}>{client.type}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className={styles.paginationWrapper}>
                      <CommonPagination
                        pageCount={Math.ceil(filteredClients.length / itemsPerPage)}
                        onPageChange={handlePageChange}
                        currentPage={currentPage}
                        show={true}
                        renderInPlace={true}
                        itemsPerPage={itemsPerPage}
                        onItemsPerPageChange={setItemsPerPage}
                        totalItems={filteredClients.length}
                      />
                    </div>
                  </>
                )}

              </div>
            )}

            {(step === 1 || (step === 0 && initialData?.id)) && (
              <>
                {userType === "client" && selectedClient && (
                  <div className={styles.selectedClientBanner}>
                    <span className={styles.bannerLabel}>Client Details:</span>
                    <span className={styles.bannerValue}>{selectedClient.name} ({selectedClient.npi || 'No NPI'})</span>
                    {!isEditMode && (
                      <button type="button" className={styles.changeClientBtn} onClick={() => setStep(3)}>Change</button>
                    )}
                  </div>
                )}
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Email *</label>
                    <input
                      type="email"
                      className={styles.input}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter email"
                      style={{
                        borderColor: errors.email ? "#ef4444" : "#d1d5db",
                      }}
                    />
                    {errors.email && (
                      <span className={styles.errorText}>{errors.email}</span>
                    )}
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Alias / Username *</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Enter Alias / Username"
                      maxLength={50}
                      style={{
                        borderColor: errors.username ? "#ef4444" : "#d1d5db",
                      }}
                    />
                    {errors.username && (
                      <span className={styles.errorText}>
                        {errors.username}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>First Name *</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Enter first name"
                      maxLength={50}
                      style={{
                        borderColor: errors.firstName ? "#ef4444" : "#d1d5db",
                      }}
                    />
                    {errors.firstName && (
                      <span className={styles.errorText}>
                        {errors.firstName}
                      </span>
                    )}
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Middle Name</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={middleName}
                      onChange={(e) => setMiddleName(e.target.value)}
                      placeholder="Enter middle name"
                      maxLength={50}
                      style={{
                        borderColor: errors.middleName ? "#ef4444" : "#d1d5db",
                      }}
                    />
                    {errors.middleName && (
                      <span className={styles.errorText}>
                        {errors.middleName}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Last Name *</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Enter last name"
                      maxLength={50}
                      style={{
                        borderColor: errors.lastName ? "#ef4444" : "#d1d5db",
                      }}
                    />
                    {errors.lastName && (
                      <span className={styles.errorText}>
                        {errors.lastName}
                      </span>
                    )}
                  </div>
                  <div className={styles.formRow}>
  <div className={styles.formGroup}>
    <label className={styles.label}>Employee ID</label>
    <input
      type="text"
      className={styles.input}
      value={empId}
      onChange={(e) => setEmpId(e.target.value)}
      placeholder="Enter employee ID"
    />
  </div>

  <div className={styles.formGroup}>
    <label className={styles.label}>Date of Birth</label>
    <input
      type="date"
      className={styles.input}
      value={dateOfBirth}
      onChange={(e) => setDateOfBirth(e.target.value)}
    />
  </div>
</div>
                  {!initialData?.id && (
                    <div className={styles.formGroup}>
                      <label className={styles.label}>Password *</label>
                      <input
                        type="password"
                        className={styles.input}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter password"
                        style={{
                          borderColor: errors.password ? "#ef4444" : "#d1d5db",
                        }}
                      />
                      {errors.password && (
                        <span className={styles.errorText}>
                          {errors.password}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Phone</label>
                  <div className={styles.phoneInputGroup}>
                    <Select
                      value={countryCodeOptions.find(
                        (opt) => opt.value === phoneCountryCode,
                      )}
                      onChange={(selected) =>
                        setPhoneCountryCode(selected?.value || "+91")
                      }
                      options={countryCodeOptions}
                      className={styles.countryCodeSelect}
                      classNamePrefix="select"
                      styles={{
                        ...getCustomSelectStyles(),
                        menu: (base) => ({
                          ...getCustomSelectStyles().menu(base),
                          zIndex: 10000,
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 10000,
                        }),
                      }}
                      isSearchable={false}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        if (value.length <= 15) {
                          setPhoneNumber(value);
                        }
                      }}
                      placeholder="10-15 digits"
                      className={styles.phoneNumberInput}
                      style={{
                        borderColor: errors.phoneNumber ? "#ef4444" : "#d1d5db",
                      }}
                    />
                  </div>
                  {errors.phoneNumber && (
                    <span className={styles.errorText}>
                      {errors.phoneNumber}
                    </span>
                  )}
                </div>
              </>
            )}

            {step === 2 && userType === "internal" && (
              <>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Roles</label>
                  <Select
                    isMulti
                    value={userRoles}
                    onChange={(selected) =>
                      setUserRoles(
                        selected as { value: string; label: string }[],
                      )
                    }
                    options={roleOptions}
                    placeholder="Assign roles to user"
                    styles={{
                      ...getCustomSelectStyles(),
                      menu: (base) => ({
                        ...getCustomSelectStyles().menu(base),
                        zIndex: 10000,
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 10000,
                      }),
                    }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Supervisor Role</label>
                  <Select
                    value={supervisorRole}
                    onChange={(role) => {
  setSupervisorRole(role);
  setSelectedSupervisor(null);
}}
                    options={roleOptions}
                    placeholder="Select role to choose supervisor from"
                    styles={{
                      ...getCustomSelectStyles(),
                      menu: (base) => ({
                        ...getCustomSelectStyles().menu(base),
                        zIndex: 10000,
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 10000,
                      }),
                    }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Supervisor</label>
                  <Select
                    value={supervisorOptions.find(
                      (o) => o.value === selectedSupervisor,
                    )}
                    onChange={(selected) =>
                      setSelectedSupervisor(selected?.value || null)
                    }
                    options={supervisorOptions}
                    isDisabled={!supervisorRole || loadingSupervisors}
                    placeholder={
                      loadingSupervisors
                        ? "Loading supervisors..."
                        : supervisorRole
                          ? "Select supervisor"
                          : "Select supervisor role first"
                    }
                    styles={{
                      ...getCustomSelectStyles(),
                      menu: (base) => ({
                        ...getCustomSelectStyles().menu(base),
                        zIndex: 10000,
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 10000,
                      }),
                    }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
              </>
            )}
          </div>
          <div className={styles.actions}>

            {/* BACK BUTTON — visible after step 0, but handle Step 3 separately or encompass it */}
            {/* BACK BUTTON */}
            {((!isEditMode && step > 0) || step === 3 || step === 2 || (isEditMode && step === 1 && allowUserTypeSelection)) && (
              <button type="button" className={styles.backButton} onClick={handleBack}>
                Back
              </button>
            )}

            {/* STEP 3 NEXT BUTTON */}
            {step === 3 && (
              <button
                type="button"
                className={styles.nextButton}
                onClick={() => {
                  if (!selectedClient) {
                    setToast({ message: 'Client selection is required', type: 'warning' });
                    return;
                  }
                  setStep(1);
                }}
              >
                Next <ChevronRight size={16} />
              </button>
            )}

            {/* INTERNAL FLOW */}
            {userType === "internal" && step === 1 && (
              <button type="button" className={styles.nextButton} onClick={handleNext}>
                Next
              </button>
            )}

            {/* CREATE BUTTON */}
            {(userType === "client" && step === 1) && (
              <button type="submit" className={styles.submitButton}>
                {isEditMode ? "Update" : "Create"}
              </button>
            )}

            {userType === "internal" && step === 2 && (
              <button type="submit" className={styles.submitButton}>
                {isEditMode ? "Update" : "Create"}
              </button>
            )}

            {toast && (
              <Toast
                message={toast.message}
                type={toast.type}
                onClose={() => setToast(null)}
              />
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;
